import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { IconButton, Table, TableBody, TableHead, TablePagination } from "@mui/material";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import {
  HeaderTableCell,
  TableCell,
  TableContainer,
  TableHeaderRow,
  TableRow,
} from "@/components/molecules/Table/styled";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "@/constants/pagination";
import { COMPANY_PERMISSIONS } from "@/constants/modules.permissions";
import useHasNoPermission from "@/hooks/use-check-permissions";
import usePopOver from "@/hooks/use-popover";
import { IClosedDays } from "@/interfaces/closed-days.interface";
import { ConfigurationTabMessages } from "@/messages/customer/configuration-tab.messages";
import { Pagination } from "@/types/page.types";
import { PaginatedResponse } from "@/types/requests.types";

import ClosedDaysPopover from "./ClosedDaysPopover";
import { formatDateDisplay } from "../../constants";

type Props = {
  items: IClosedDays[];
  messages: ConfigurationTabMessages;
  data: PaginatedResponse<IClosedDays>;
  pagination: Pagination;
  onRemove: (item: IClosedDays) => void;
  onEdit: (item: IClosedDays) => void;
  onOpenCreate: () => void;
};

function ClosedDaysTableRow({
  item,
  index,
  messages,
  onRemove,
  onEdit,
}: {
  item: IClosedDays;
  index: number;
  messages: ConfigurationTabMessages;
  onRemove: (item: IClosedDays) => void;
  onEdit: (item: IClosedDays) => void;
}) {
  const { openPopover, anchorEl, closePopover, isOpen, selected } = usePopOver<IClosedDays>();

  return (
    <TableRow index={index}>
      <TableCell>{formatDateDisplay(item.closedDay)}</TableCell>
      <TableCell>{item.description}</TableCell>
      <TableCell>
        <IconButton onClick={(e) => openPopover(e, item)}>
          <MoreHorizIcon />
        </IconButton>
        {selected && (
          <ClosedDaysPopover
            isOpen={isOpen}
            anchorEl={anchorEl}
            onClose={closePopover}
            messages={messages}
            selected={selected}
            onRemove={onRemove}
            onEdit={onEdit}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function ClosedDaysTable({
  items,
  messages,
  data,
  pagination,
  onRemove,
  onEdit,
  onOpenCreate,
}: Props) {
  const hasNoPermissionToEdit = useHasNoPermission(COMPANY_PERMISSIONS.EDIT);

  return (
    <MDBox width="100%">
      <MDBox display="flex" justifyContent="flex-end" mb={1}>
        <MDButton
          variant="gradient"
          color="dark"
          endIcon={<AddCircleOutlineIcon />}
          onClick={onOpenCreate}
          disabled={hasNoPermissionToEdit}
        >
          {messages.closedDaysNewButton}
        </MDButton>
      </MDBox>

      <TableContainer>
        <Table stickyHeader aria-label="closed-days-table">
          <TableHead>
            <TableHeaderRow>
              <HeaderTableCell>{messages.closedDaysDateColumn}</HeaderTableCell>
              <HeaderTableCell>{messages.closedDaysDescriptionColumn}</HeaderTableCell>
              <HeaderTableCell>{messages.closedDaysOptionsColumn}</HeaderTableCell>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow index={0}>
                <TableCell colSpan={3} align="center">
                  <MDTypography variant="body2">{messages.closedDaysEmptyMessage}</MDTypography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item, index) => (
                <ClosedDaysTableRow
                  key={item.id}
                  item={item}
                  index={index}
                  messages={messages}
                  onRemove={onRemove}
                  onEdit={onEdit}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        labelRowsPerPage={messages.rowsPerPage}
        rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={data.totalCount}
        onRowsPerPageChange={(e) => pagination.extend(Number(e.target.value))}
        rowsPerPage={pagination.pageSize}
        page={pagination.pageNumber - 1}
        onPageChange={(_, pageNumber) => pagination.changePage(pageNumber)}
      />
    </MDBox>
  );
}

export default ClosedDaysTable;
